import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Confetti from 'react-confetti';
import { Button } from '@trevor/ui-components';
import {
  currentTimezone,
  morning8am,
  refreshFrequency,
  showTime,
} from '@/util/helpers/time-helpers';
import { get, post, postDelete, userAction } from '@/util/helpers/ajax-helpers';
import ModalContent from './ModalContent';
import SelectHour from './SelectHour';
import style from '../css/module/dashboard-push.css';
import shareInsight from '../css/module/share-insight.css';
import flex from '../css/module/flex.css';
import EmailInvites from './EmailInvites';
import SelectSlackChannel from './SelectSlackChannel';
import DropdownItem from '../util/DropdownItem';
import MultiDropdown from './MultiDropdown';
import DropdownButton from './DropdownButton';
import { EMAIL, SLACK } from '../util/AlertRef';
import AjaxSpinner from './AjaxSpinner';
import { DAILY } from '../util/enums/EnumDashboardRefreshFrequency';

export const MONDAY = 'monday';
export const TUESDAY = 'tuesday';
export const WEDNESDAY = 'wednesday';
export const THURSDAY = 'thursday';
export const FRIDAY = 'friday';
export const SATURDAY = 'saturday';
export const SUNDAY = 'sunday';
export const WEEKLY = 'weekly';

const daysOfWeek = [
  MONDAY,
  TUESDAY,
  WEDNESDAY,
  THURSDAY,
  FRIDAY,
  SATURDAY,
  SUNDAY,
];

const FREQUENCY = 'frequency';
const DAY_OF_WEEK = 'dayOfWeek';
const HOUR = 'hour';
const DESTINATION = 'destination';
const SLACK_CHANNEL = 'slackChannel';
const EMAIL_RECIPIENTS = 'emailRecipients';

const TEMPLATE_DELIVERY = {
  existingId: null,
  frequency: WEEKLY,
  dayOfWeek: MONDAY,
  hour: morning8am(),
  destination: SLACK,
  emailRecipients: [],
  slackChannel: null,
  isDisabled: false,
};

const TEMPLATE_TEST = {
  destination: SLACK,
  emailRecipients: [],
  slackChannel: null,
};

class SendDashboard extends Component {
  state = {
    initialLoading: true,
    loading: false,
    existingDeliveries: null, // list of TEMPLATE_DELIVERY objs
    deliveryBeingEdited: null, // id of the one in edit mode
    loadingSlackDetails: true,
    slackConnected: false,
    delivery: TEMPLATE_DELIVERY, // a new delivery or existing one being edited
    showAddNewWizard: true,
    showTest: false,
    sendingTest: false,
    testSuccess: false,
    testMessage: TEMPLATE_TEST,
  };

  componentDidMount() {
    const { defaultDestination } = this.props;

    this.loadSchedules(true);

    if (defaultDestination) {
      const updatedTestTemplate = {
        ...this.state.testMessage,
        destination: this.props.defaultDestination,
      };
      this.setState({
        testMessage: updatedTestTemplate,
      });
    }
  }

  sendTest = () => {
    const { destination, emailRecipients, slackChannel } =
      this.state.testMessage;
    const dashboardId = this.props.dashboard.id();
    userAction(`deliver-single-dashboard-snapshot-${destination}`);
    this.setState({
      sendingTest: true,
    });
    post('sending snapshot', `/dashboards/${dashboardId}/send-snapshot`, {
      destination,
      recipients: destination === SLACK ? [slackChannel] : emailRecipients,
    })
      .then(() => {
        const templateState = {
          ...TEMPLATE_TEST,
          destination: this.state.testMessage.destination,
        };
        this.setState({
          sendingTest: false,
          testSuccess: true,
          testMessage: templateState,
        });
        setTimeout(() => {
          this.setState({
            testSuccess: false,
          });
        }, 4000);
      })
      .catch(() => {
        this.setState({
          sendingTest: false,
        });
      });
  };

  loadSchedules = (showWizard) => {
    const copyOfTemplate = { ...TEMPLATE_DELIVERY };

    copyOfTemplate.destination =
      this.props.defaultDestination || copyOfTemplate.destination;

    // reset wizard
    this.setState({
      deliveryBeingEdited: null,
      delivery: copyOfTemplate,
      showAddNewWizard: showWizard || this.state.existingDeliveries === 0,
    });
    get(
      'loading dashboard schedules',
      `/dashboards/${this.props.dashboard.id()}/schedules`,
    ).then((json) => {
      const schedules = [];
      json.schedules.map((s) => {
        schedules.push({
          ...TEMPLATE_DELIVERY,
          existingId: s.id,
          frequency: s.frequency === DAILY ? DAILY : WEEKLY,
          dayOfWeek: s.frequency === DAILY ? MONDAY : s.frequency, // frequency is either daily or a day of the week
          hour: s.run_at,
          destination: s.destination,
          emailRecipients: s.destination !== EMAIL ? [] : s.recipients,
          slackChannel: s.destination !== SLACK ? null : s.recipients[0],
        });
      });
      this.setState({
        initialLoading: false,
        loading: false,
        existingDeliveries: schedules,
        showAddNewWizard: showWizard || schedules.length === 0,
      });
    });
  };

  updateDeliveryState = (newValue, stateToUpdate, isTest) => {
    const copyOfObj = {
      ...(isTest ? this.state.testMessage : this.state.delivery),
    };
    copyOfObj[stateToUpdate] = newValue;
    this.setState({
      [isTest ? 'testMessage' : 'delivery']: copyOfObj,
    });
  };

  onSubmit = () => {
    const { dashboard } = this.props;

    const { deliveryBeingEdited, existingDeliveries, delivery } = this.state;

    userAction(`use-add-dashboard-delivery-${delivery.destination}`);

    const existingId =
      existingDeliveries[deliveryBeingEdited]?.existingId ?? null;

    this.setState({
      loading: true, // show loading icon
    });
    post(
      'saving dashboard schedule',
      `/dashboards/${dashboard.id()}/schedules`,
      {
        existingScheduleId: existingId,
        destination: delivery.destination,
        frequency: delivery.frequency === DAILY ? DAILY : delivery.dayOfWeek, // frequency is either daily or a day of the week
        time: delivery.hour,
        timezone: currentTimezone(),
        recipients:
          delivery.destination === EMAIL
            ? delivery.emailRecipients
            : [delivery.slackChannel],
      },
    ).then(() => {
      // reload schedules
      this.loadSchedules();
      // reload schedule tags on dashboard page
      if (this.props.reloadSchedules) {
        this.props.reloadSchedules();
      }
    });
  };

  onEdit = (i) => {
    const newObj = { ...this.state.existingDeliveries[i] };
    this.setState({
      deliveryBeingEdited: i,
      delivery: newObj,
      showAddNewWizard: false,
    });
  };

  onCancel = () => {
    this.setState({
      deliveryBeingEdited: null,
      delivery: TEMPLATE_DELIVERY,
    });
  };

  onDelete = (indexToDelete) => {
    const { existingDeliveries } = this.state;

    const delivery = existingDeliveries[indexToDelete];
    this.setState({
      loading: true, // show loading icon
    });
    postDelete(
      'deleting dashboard schedule',
      `/dashboards/${this.props.dashboard.id()}/schedules/${
        delivery.existingId
      }`,
    ).then(() => {
      // reload schedules
      this.loadSchedules();
      // reload schedule tags on dashboard page
      if (this.props.reloadSchedules) {
        this.props.reloadSchedules();
      }
    });
  };

  buildSelectMenu = (optionsArray, stateToUpdate, maxWidth, isTest) => {
    const { isLite } = this.props;

    const options = optionsArray.map((o, i) => {
      const isDisabledOption =
        stateToUpdate === FREQUENCY && o === DAILY && isLite;

      const text = isDisabledOption ? `${o} (upgrade to Pro)` : o;

      return (
        <option disabled={isDisabledOption} key={i} value={o}>
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </option>
      );
    });
    const _style = maxWidth
      ? { maxWidth: `${maxWidth}px`, minWidth: `${maxWidth}px` }
      : {};
    return (
      <select
        style={_style}
        className="form-control"
        value={
          isTest
            ? this.state.testMessage[stateToUpdate]
            : this.state.delivery[stateToUpdate]
        }
        onChange={(e) =>
          this.updateDeliveryState(e.target.value, stateToUpdate, isTest)
        }
      >
        {options}
      </select>
    );
  };

  disableSave = (isTest) => {
    const { delivery, testMessage, loading } = this.state;
    if (loading) {
      return true;
    }
    const obj = isTest ? testMessage : delivery;
    const destination = obj[DESTINATION];
    return destination === SLACK
      ? !obj[SLACK_CHANNEL] // no channel added
      : obj[EMAIL_RECIPIENTS].length === 0; // no emails added
  };

  wizard = () => {
    const { delivery, deliveryBeingEdited } = this.state;
    const { dashboard } = this.props;
    return (
      <tr>
        <td>
          <div className={style.innerCell}>
            {deliveryBeingEdited !== null ? (
              <div className={flex.alignItemsCenter}>
                <div className={style.iconContainer}>
                  <img
                    src={
                      delivery.destination === SLACK
                        ? '/img/slack-min.png'
                        : '/img/email-min.png'
                    }
                  />
                </div>
                <span>
                  {delivery.destination === SLACK ? 'Slack' : 'Email'}
                </span>
              </div>
            ) : (
              this.buildSelectMenu([SLACK, EMAIL], DESTINATION, 73)
            )}
          </div>
        </td>
        <td>
          <div className={style.innerCell}>
            <div className={flex.column}>
              <div className={flex.alignItemsCenter}>
                {this.buildSelectMenu([DAILY, WEEKLY], FREQUENCY, 85)}
                {this.state.delivery.frequency === WEEKLY ? (
                  <div className={flex.alignItemsCenter}>
                    on {this.buildSelectMenu(daysOfWeek, DAY_OF_WEEK)}
                  </div>
                ) : null}
                at
                <SelectHour
                  onChange={(value) => this.updateDeliveryState(value, HOUR)}
                  chosenTime={this.state.delivery.hour}
                  hideTimeZone
                />
              </div>
              <small style={{ marginTop: '10px', paddingLeft: '7px' }}>
                Your dashboard refreshes {refreshFrequency(dashboard)}
              </small>
            </div>
          </div>
        </td>
        <td>
          <div className={style.innerCell}>
            {this.state.delivery.destination === EMAIL ? (
              <EmailInvites
                stagingRecipients={this.state.delivery.emailRecipients}
                setStagingRecipients={(newArray) =>
                  this.updateDeliveryState(newArray, EMAIL_RECIPIENTS)
                }
                submitButtonText="Add"
                inputStyle={{ maxWidth: '250px' }}
              />
            ) : (
              <SelectSlackChannel
                setStagingChannel={(channel) =>
                  this.updateDeliveryState(channel, SLACK_CHANNEL)
                }
                stagingChannel={this.state.delivery.slackChannel}
                dashboardDelivery={false}
                isDashboardDelivery
                loadingSlackDetails={this.state.loadingSlackDetails}
                slackConnected={this.state.slackConnected}
                slackConnectionResult={(json) =>
                  this.setState({
                    loadingSlackDetails: false,
                    slackConnected: json.connected,
                  })
                }
              />
            )}
          </div>
        </td>
        <td>
          <div className={`${style.innerCell} ${flex.justifyContentEnd}`}>
            {deliveryBeingEdited !== null && (
              <Button
                variant="secondary"
                className="margin-right-5"
                onClick={this.onCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={() => this.onSubmit()}
              disabled={this.disableSave()}
            >
              {deliveryBeingEdited === null ? 'Save new' : 'Save'}
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  existingDeliveries = () => {
    const { existingDeliveries, deliveryBeingEdited } = this.state;
    if (existingDeliveries.length === 0) {
      return null;
    }

    const activeClass = shareInsight.active;

    return existingDeliveries.map((delivery, i) => {
      if (deliveryBeingEdited === i) {
        return this.wizard();
      }

      const onEdit = this.state.loading ? () => null : () => this.onEdit(i);

      return (
        <tr key={i}>
          <td>
            <div className={style.innerCell}>
              <div className={flex.alignItemsCenter}>
                <div className={style.iconContainer}>
                  <img
                    src={
                      delivery.destination === SLACK
                        ? '/img/slack-min.png'
                        : '/img/email-min.png'
                    }
                  />
                </div>
                <span>
                  {delivery.destination === SLACK ? 'Slack' : 'Email'}
                </span>
              </div>
            </div>
          </td>
          <td>
            <div className={style.innerCell}>
              <span
                onClick={onEdit}
                className={`${shareInsight.tag} ${activeClass}`}
              >
                {delivery.frequency}
              </span>
              {delivery.frequency === DAILY ? null : (
                <span>
                  on{' '}
                  <span
                    onClick={onEdit}
                    className={`${shareInsight.tag} ${activeClass}`}
                  >
                    {delivery.dayOfWeek}
                  </span>
                </span>
              )}
              <span>at&nbsp;</span>
              <span
                onClick={onEdit}
                className={`${shareInsight.tag} ${activeClass}`}
              >
                {showTime(delivery.hour)}
              </span>
            </div>
          </td>
          <td>
            <div className={style.innerCell}>
              <span className={flex.wrap}>
                {delivery.destination === SLACK ? (
                  <span
                    onClick={onEdit}
                    className={`${shareInsight.tag} ${activeClass}`}
                  >
                    {delivery.slackChannel}
                  </span>
                ) : (
                  delivery.emailRecipients.map((email, index) => {
                    return (
                      <span
                        onClick={onEdit}
                        key={index}
                        className={`${shareInsight.tag} ${activeClass}`}
                      >
                        {email}
                      </span>
                    );
                  })
                )}
              </span>
            </div>
          </td>
          <td>
            <div
              style={{ height: '62px' }}
              className={`${style.innerCell} ${flex.justifyContentEnd}`}
            >
              {this.state.loading ? null : (
                <DropdownButton
                  contentBuilder={() => (
                    <MultiDropdown
                      items={[
                        DropdownItem.action(<span>Edit</span>, () =>
                          this.onEdit(i),
                        ),
                        DropdownItem.dropdownWithConfirmation(
                          <span>Delete</span>,
                          () => this.onDelete(i),
                        ),
                      ]}
                    />
                  )}
                >
                  <div className={style.boxControl}>
                    <FontAwesomeIcon icon="ellipsis-v" />
                  </div>
                </DropdownButton>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  explanation = () => {
    // the width + negative margin here cause the explanation container to touch the sides of the modal, which looks much better.
    const _style = {
      marginLeft: '-10px',
      marginRight: '-10px',
      width: 'calc(100% + 20px)',
    };

    return (
      <div className={shareInsight.alertExplanationContainer} style={_style}>
        <div className={shareInsight.shareOption}>
          <div className={`${flex.row} ${flex.alignItemsCenter}`}>
            <div className={shareInsight.showcaseImg}>
              <div className={shareInsight.showcasePreviewContainer}>
                <img
                  width="300"
                  height="157"
                  style={{ maxWidth: '300px', borderRadius: '2px' }}
                  src="/img/dboard-min.png"
                />
              </div>
            </div>
            <div className={shareInsight.showcaseText}>
              <h5>
                <b>What you'll receive</b>
              </h5>
              <span style={{ fontSize: '1.1em' }}>
                A snapshot of your dashboard, sent to you at regular intervals
                via email or Slack
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { dashboard } = this.props;
    const { showAddNewWizard } = this.state;

    return (
      <ModalContent
        header={`Deliver snapshot: ${dashboard.name()}`}
        loading={false}
        medium
        onClose={() => null}
        onSubmit={() => null}
        disabled={false}
        explanation={this.explanation()}
      >
        {this.state.initialLoading ? (
          <div className={`full-width ${flex.justifyContentCenter}`}>
            <AjaxSpinner />
          </div>
        ) : (
          <div
            className={`${flex.column} ${style.pageContainer} ${shareInsight.mainContent}`}
          >
            <table>
              <thead>
                <tr>
                  <td>
                    <div className={style.innerCell}>Destination</div>
                  </td>
                  <td>
                    <div className={style.innerCell}>
                      Schedule&nbsp;
                      <span style={{ fontWeight: 'normal' }}>
                        (timezone: {currentTimezone()})
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={style.innerCell}>Recipient(s)</div>
                  </td>
                  <td />
                </tr>
              </thead>
              <tbody>
                {this.existingDeliveries()}
                {showAddNewWizard ? this.wizard() : null}
              </tbody>
            </table>
            <div
              className={`${flex.justifyContentBetween} margin-top-20 ${flex.alignItemsCenter}`}
            >
              <div
                className={`${style.testArea} user-clickable position-relative`}
                onClick={() =>
                  this.setState({ showTest: !this.state.showTest })
                }
              >
                {this.state.testSuccess ? (
                  <Confetti
                    width={450}
                    height={116}
                    gravity={0.15}
                    opacity={0.25}
                    initialVelocityY={20}
                  />
                ) : null}
                <div
                  className={`w-100 ${flex.alignItemsCenter} ${flex.justifyContentBetween}`}
                >
                  <span className="margin-right-5">
                    <b>Deliver a single snapshot (e.g. as a test)</b>
                  </span>
                  {!this.state.showTest ? (
                    <FontAwesomeIcon icon="chevron-right" />
                  ) : (
                    <div className={style.formatOpen}>
                      <FontAwesomeIcon icon="chevron-right" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                {!showAddNewWizard && !this.state.loading ? (
                  <Button
                    onClick={() =>
                      this.setState({
                        showAddNewWizard: true,
                        deliveryBeingEdited: null,
                        delivery: TEMPLATE_DELIVERY,
                      })
                    }
                    disabled={this.state.loading}
                  >
                    Add new delivery
                  </Button>
                ) : this.state.loading ? (
                  <div className={flex.alignItemsCenter}>
                    <AjaxSpinner small />
                  </div>
                ) : null}
              </div>
            </div>
            <div className={flex.justifyContentBetween}>
              {!this.state.showTest ? null : (
                <div
                  className={`${style.testArea} ${flex.row} ${flex.justifyContentBetween}`}
                >
                  {this.state.sendingTest ? (
                    <div className={flex.alignItemsCenter}>
                      Sending snapshot. This can take up to 20 seconds
                    </div>
                  ) : (
                    <div className={`${flex.alignItemsStart} margin-right-10`}>
                      <div className={`${flex.inline} margin-right-10`}>
                        {this.buildSelectMenu(
                          [SLACK, EMAIL],
                          DESTINATION,
                          73,
                          true,
                        )}
                      </div>
                      {this.state.testMessage.destination === SLACK ? (
                        <SelectSlackChannel
                          setStagingChannel={(channel) =>
                            this.updateDeliveryState(
                              channel,
                              SLACK_CHANNEL,
                              true,
                            )
                          }
                          stagingChannel={this.state.testMessage.slackChannel}
                          dashboardDelivery={false}
                          isDashboardDelivery
                          loadingSlackDetails={this.state.loadingSlackDetails}
                          slackConnected={this.state.slackConnected}
                          slackConnectionResult={(json) =>
                            this.setState({
                              loadingSlackDetails: false,
                              slackConnected: json.connected,
                            })
                          }
                        />
                      ) : (
                        <EmailInvites
                          stagingRecipients={
                            this.state.testMessage.emailRecipients
                          }
                          setStagingRecipients={(newArray) =>
                            this.updateDeliveryState(
                              newArray,
                              EMAIL_RECIPIENTS,
                              true,
                            )
                          }
                          submitButtonText="Add"
                          inputStyle={{ maxWidth: '250px' }}
                        />
                      )}
                    </div>
                  )}
                  {this.state.sendingTest ? (
                    <div className={flex.alignItemsCenter}>
                      <AjaxSpinner small />
                    </div>
                  ) : (
                    <div
                      className={`position-relative ${flex.justifyContentCenter}`}
                    >
                      <Button
                        onClick={this.sendTest}
                        className="position-relative"
                        disabled={
                          this.disableSave(true) || this.state.sendingTest
                        }
                      >
                        Send
                      </Button>
                      {!this.state.testSuccess ? null : (
                        <div className={style.testSuccess}>Sent!</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div />
            </div>
          </div>
        )}
      </ModalContent>
    );
  }
}

const mapStateToProps = (state) => ({
  isLite: state.datasource.isLite,
});

export default connect(mapStateToProps)(SendDashboard);
